import { defineStore } from "pinia";
import { ref } from "vue";

import type { AlmSourceId, BoardType, IdMap, StatusClass } from "@/model/baseTypes";
import type { Board } from "@/model/board";
import type { Card } from "@/model/card";
import type { AlmItemStatus, AlmItemType, AlmItemTypeMap } from "@/model/status";
import type { StickyType } from "@/model/stickyType";
import { asReadonly } from "@/utils/vue";

import {
  defaultStatuses,
  dynamicStatus,
  fallbackStatus,
  initialDefaultStatus,
} from "./helpers/almItemStatus";

const emptyAlmItemType = { statuses: [], globalTargetStatuses: [], dynamic: false };

export const useAlmItemTypeStore = defineStore("almItemTypes", () => {
  const almItemTypes = ref<IdMap<AlmItemTypeMap>>({});

  function calcStatus(
    status: string | null | undefined,
    type: StickyType,
    teamId: string | null | undefined,
    artId: string | null | undefined,
    almSourceId: AlmSourceId | null,
    statusClass?: StatusClass,
  ): AlmItemStatus | undefined {
    if (type.functionality !== "workitem") {
      return;
    }
    const almItemType = findAlmItemType(type, teamId, artId, almSourceId);
    return status ? findStatus(almItemType, status, statusClass) : findInitialStatus(almItemType);
  }

  function findStatusForCard(
    status: string,
    card: Card,
    board: Board,
    statusClass?: StatusClass,
  ): AlmItemStatus {
    const almItemType = findAlmItemType(
      card.type,
      card.teamId,
      board.artId ?? card.artId,
      card.almSourceId,
    );
    return findStatus(almItemType, status, statusClass);
  }

  function findAlmItemType(
    type: StickyType,
    teamId: string | null | undefined,
    artId: string | null | undefined,
    almSourceId: AlmSourceId | null,
  ): AlmItemType {
    const boardKey = getBoardKey(type.origin, artId ?? undefined, teamId ?? undefined);
    const types = almItemTypes.value[type.id] || {};
    const mappingAlmItemType = types[boardKey ?? ""] || types[""];
    return mappingAlmItemType?.[almSourceId ?? ""] || mappingAlmItemType?.[""] || emptyAlmItemType;
  }

  function updateAlmItemType(card: Card, board: Board, almItemType: AlmItemType) {
    const existing = findAlmItemType(card.type, card.teamId, board.artId, card.almSourceId);
    existing.globalTargetStatuses = almItemType.globalTargetStatuses;
    existing.statuses = almItemType.statuses;
  }

  function setAlmItemType(id: string, almItemType: AlmItemTypeMap) {
    almItemTypes.value[id] = almItemType;
  }

  return {
    almItemTypes: asReadonly(almItemTypes),
    calcStatus,
    findStatusForCard,
    findAlmItemType,
    updateAlmItemType,
    setAlmItemType,
  };
});

function getBoardKey(
  boardType: BoardType,
  artId: string | undefined,
  teamId: string | undefined,
): string | undefined {
  switch (boardType) {
    case "backlog":
      return artId;
    case "team":
      return teamId;
  }
}

function findInitialStatus(almItemType: AlmItemType): AlmItemStatus {
  return almItemType.statuses.find((s) => s.initial) ?? initialDefaultStatus;
}

function findStatus(
  almItemType: AlmItemType,
  status: string,
  statusClass?: StatusClass,
): AlmItemStatus {
  // if no ALM tool -> default statuses
  if (almItemType.statuses.length === 0) {
    if (almItemType.dynamic) {
      return dynamicStatus(status, statusClass);
    }
    return defaultStatuses.find((s) => s.name === status) ?? initialDefaultStatus;
  }
  const almItemStatus = almItemType.statuses.find((s) => s.name === status);
  // if alm tool & status not in the (possibly stale) list -> keep the class the backend sent,
  // "undefined" when it sent none
  return almItemStatus ?? fallbackStatus(almItemType, status, statusClass);
}
